import {
  CHECKOUT_JUST_DELETED_LAST_MESSAGE,
  CHECKOUT_RECEIVE_ADDRESSES,
  CHECKOUT_RECEIVE_CONFIGURE_ADDRESES,
  CHECKOUT_SHOW_MAYBE_PO_BOX_HELPER_TEXT,
  CHECKOUT_STORE_ADDRESS_ALERT_MSG,
  CLEAR_CHECKOUT_SUGGESTED_ADDRESSES,
  NEW_ADDRESS_PRIMARY,
  RECEIVE_NEW_SHIPPING_FIELD,
  SET_CHECKOUT_ADDRESSES_LOADING,
  SET_CHECKOUT_SUGGESTED_ADDRESSES,
  SET_INVALID_ADDRESS_FIELDS,
  SET_SELECTED_SUGGESTED_ADDRESS,
  VALIDATE_ADDRESS_FIELDS
} from 'constants/reduxActions';
import {
  ADDRESS_STEP,
  CHECKOUT_STEP_MAP,
  EDIT_ADDRESS_STEP,
  EDIT_BILLING_ADDRESS_STEP,
  NEW_ADDRESS_STEP,
  NEW_BILLING_ADDRESS_STEP,
  NEW_PAYMENT_STEP,
  PAYMENT_STEP,
  TBD_STEP
} from 'constants/checkoutFlow';

import {
  catchHandlerFactory,
  fetchCheckoutData,
  hasFieldErrors,
  pushUrl,
  redirectUrl,
  setCheckoutConfigureIsProcessing
} from 'actions/checkout/purchase';
import { makePaymentPrimary, updatePaymentAddress } from 'actions/checkout/payments';
import { setSessionCookies } from 'actions/session';
import {
  addresses,
  changeAddress,
  checkoutData,
  deleteAddress,
  savePayment
} from 'apis/mafia';
import {
  fetchApiNotAuthenticatedMiddleware,
  fetchErrorMiddleware,
  fetchMafiaValidationErrorMiddleware
} from 'middleware/fetchErrorMiddleware';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';

export function defaultEndpoints() {
  return {
    addresses,
    changeAddress,
    deleteAddress,
    checkoutData,
    savePayment
  };
}

export function setShowMaybePoBoxHelperText(showPoBoxHelperText) {
  return { type: CHECKOUT_SHOW_MAYBE_PO_BOX_HELPER_TEXT, showPoBoxHelperText };
}

export function clearSuggestedAddresses() {
  return { type: CLEAR_CHECKOUT_SUGGESTED_ADDRESSES };
}

export function setJustDeletedLastAddress() {
  return { type: CHECKOUT_JUST_DELETED_LAST_MESSAGE };
}

export function newAddressPrimary(isNewPrimary) {
  return { type: NEW_ADDRESS_PRIMARY, isNewPrimary };
}

export function receiveCheckoutConfigureAddresses(data, hasInvalidAddress) {
  return { type: CHECKOUT_RECEIVE_CONFIGURE_ADDRESES, data, hasInvalidAddress };
}

export function receiveCheckoutAddresses(data) {
  return { type: CHECKOUT_RECEIVE_ADDRESSES, data };
}

export function receiveNewShippingField(fieldKey, fieldValue) {
  return { type: RECEIVE_NEW_SHIPPING_FIELD, fieldKey, fieldValue };
}

export function setAddressesAreLoadingMessage(loadingMessage) {
  return { type: SET_CHECKOUT_ADDRESSES_LOADING, loadingMessage };
}

export function setSelectedSuggestedAddress(selectedAddressId) {
  return { type: SET_SELECTED_SUGGESTED_ADDRESS, selectedAddressId };
}

export function setSuggestedAddresses({ isBilling, selectedAddressId, suggestedAddresses }) {
  return { type: SET_CHECKOUT_SUGGESTED_ADDRESSES, isBilling, selectedAddressId, suggestedAddresses };
}

export function setInvalidAddressFields(invalidFields) {
  return { type: SET_INVALID_ADDRESS_FIELDS, invalidFields };
}

export function storeAddressAlertMessage(addressAlertMsg) {
  return { type: CHECKOUT_STORE_ADDRESS_ALERT_MSG, addressAlertMsg };
}

export function validateAddressFields() {
  return { type: VALIDATE_ADDRESS_FIELDS };
}

export function fetchAddresses(mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();
    dispatch(setAddressesAreLoadingMessage('Loading your address information...'));
    return mafiaEndpoints.addresses(mafia, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        dispatch(receiveCheckoutAddresses(response));
      })
      .catch(catchHandlerFactory(dispatch, CHECKOUT_STEP_MAP[TBD_STEP]));
  };
}

export function deleteCheckoutAddress(info = {}, bypassConfirmation = false, mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    const { addressId } = info;

    const {
      checkoutData,
      environmentConfig: { api: { mafia } },
      routing: { locationBeforeTransitions: { search: qs } }
    } = getState();

    const {
      addresses: { saved: savedAddresses },
      purchaseData: { data: { shippingAddressId }, purchaseBillingAddressId }
    } = checkoutData;

    // bypass in place for our tests
    if (!bypassConfirmation) {
      const performDelete = confirm('Are you sure you want to delete this address?  This action cannot be undone.');
      if (!performDelete) {
        return;
      }
    }

    const addressInfo = savedAddresses.find(item => item.addressId === addressId);

    const {
      mailingAddress: { addressLine1, addressLine2, postalCode, city, stateOrRegion, countryName },
      name: { fullName }
    } = addressInfo;

    const addressLine2Text = addressLine2 ? `${addressLine2}, ` : '';
    const addressString = `${fullName}, ${addressLine1}, ${addressLine2Text}${city}, ${stateOrRegion} ${postalCode}, ${countryName}`;

    dispatch(setAddressesAreLoadingMessage('Deleting your address...'));
    dispatch(setCheckoutConfigureIsProcessing(true));
    return mafiaEndpoints.deleteAddress(mafia, addressId)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(() => {
        // clear out any errors due to the address being invalid
        dispatch(clearAddressErrors());

        const extraStepsOnDeleteParams = {
          justDeletedLastAddress: savedAddresses.length === 1,
          alertMessage: `${addressString} has been deleted.`,
          targetUrl: `${CHECKOUT_STEP_MAP[ADDRESS_STEP]}${qs}`
        };

        if (addressId === purchaseBillingAddressId || addressId === shippingAddressId) {
          return dispatch(fetchCheckoutData({ extraStepsOnDeleteParams, showFullPageSpinner: false, includePaymentsAndAddresses: true }, mafiaEndpoints));
        } else {
          dispatch(setCheckoutConfigureIsProcessing(false));
          dispatch(receiveNewShippingField('addressId', null));
          dispatch(fetchAddresses(mafiaEndpoints));
          dispatch(extraStepsForAddressDelete(extraStepsOnDeleteParams));
        }
      })
      .catch(catchHandlerFactory(dispatch, CHECKOUT_STEP_MAP[TBD_STEP]));
  };
}

export function extraStepsForAddressDelete(params) {
  return function (dispatch) {
    const { justDeletedLastAddress, alertMessage, targetUrl } = params;

    // if we just deleted the last address, we still want to stay on the address list page
    if (justDeletedLastAddress) {
      dispatch(setJustDeletedLastAddress());
    }

    // message for the address that was deleted
    dispatch(storeAddressAlertMessage(alertMessage));

    // this push is for desktop, as mobile has the delete right on the address list page
    dispatch(pushUrl(targetUrl));
  };
}

export function addNewAddress(primaryPayment = {}, mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    dispatch(validateAddressFields());

    const {
      checkoutData: {
        addresses: { new: newAddr, isNewPrimary, invalidFields },
        purchaseData: { data: { purchaseId }, targetStep } },
      environmentConfig: { api: { mafia } }
    } = getState();

    if (!newAddr || (newAddr.errors && hasFieldErrors(newAddr.errors))) {
      return;
    }

    const isBilling = targetStep === EDIT_BILLING_ADDRESS_STEP || targetStep === NEW_BILLING_ADDRESS_STEP;
    const isNewAddress = targetStep === NEW_ADDRESS_STEP || targetStep === ADDRESS_STEP;
    const isNewPayment = targetStep === NEW_PAYMENT_STEP || targetStep === PAYMENT_STEP;
    const { addressId, addressLine1, addressLine2, city, countryCode, fullName, postalCode, primaryVoiceNumber, stateOrRegion } = newAddr;

    // our state defaults isNewPrimary to true, so consider the flag only if:
    // - we are on new address screen
    // - we are on new payment screen (making payment primary requires that we have a primary address), add in check here that we do not already have a primary address
    const isDefaultShippingAddress = (isNewAddress && isNewPrimary) || (isNewPayment && isNewPrimary) || false;

    const address = {
      addressId: addressId && addressId.length ? addressId : null,
      isDefaultShippingAddress,
      mailingAddress: { addressLine1, addressLine2, city, countryCode, postalCode, stateOrRegion },
      name: { fullName },
      phone: { voice: { primary: { number: primaryVoiceNumber } } }
    };

    // if user already informed that address as invalid, then allow them to force it through
    const forceOriginal = !!(invalidFields && invalidFields.length);

    dispatch(setCheckoutConfigureIsProcessing(true));
    return mafiaEndpoints.changeAddress({ mafia, address, forceOriginal })
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        // if we want to make a payment primary, we must have a primary address, so there is some chaining
        // that happens once a payment is added to get it to be primary: addNewPayment -> addNewAddress (makes address primary) -> makePaymentPrimary
        if (primaryPayment.hasOwnProperty('name') && primaryPayment.hasOwnProperty('paymentInstrumentId')) {
          return dispatch(makePaymentPrimary({ name: primaryPayment.name, paymentInstrumentId: primaryPayment.paymentInstrumentId }, mafiaEndpoints));
        } else {

          const suggestedAddressParams = {
            isBilling,
            selectedAddressId: response.addressId,
            suggestedAddresses: response.suggestedAddresses
          };

          dispatch(setSuggestedAddresses(suggestedAddressParams));

          // valid will be false if has suggested addresses, or it was invalid address
          // only baking in support for shipping addresses right now
          const advanceOnSuccess = isBilling ? !response.suggestedAddresses.length : response.valid;

          if (isBilling) {
            const params = { advanceOnSuccess, billingAddressId: response.addressId, mafiaEndpoints };
            return dispatch(updatePaymentAddress(params));
          } else {
            const callback = () => {
              if (!response.valid && !response.suggestedAddresses.length) {
                // kick them back to editing the address that was just created
                dispatch(receiveNewShippingField('addressId', response.addressId));
                dispatch(redirectUrl(`${CHECKOUT_STEP_MAP[EDIT_ADDRESS_STEP]}?pid=${purchaseId}&editDestinationId=${response.addressId}`));
                dispatch(setInvalidAddressFields(response.invalidFields));
              }
            };

            const params = {
              addressId: response.addressId,
              advanceOnSuccess,
              advanceOnSuccessAsRedirect: true, // redirect url vs push url
              callback,
              hasInvalidAddress: !!response.invalidFields,
              includePaymentsAndAddresses: true,
              showFullPageSpinner: false
            };

            // We are going to redirect the url rather than push the url after
            // editing an address.  The issue is that if the user hit the back button,
            // the address that would be loaded on the page would be deactivated as
            // making an edit to an address creates a new address. Doing the redirect
            // vs push will prevent the user from trying to edit a deactivated address
            // and they will now be taken to the address list page instead.

            // Clearing the selected address will allow the address list component to
            // properly determine which address should be pre-selected, as what was
            // stored previously was the deativated address. Only clear if there were
            // no invalid fields, as user will be kept on the same page
            const { invalidFields = [] } = response;
            if (!(invalidFields && invalidFields.length)) {
              dispatch(clearSelectedAddress());
            }
            return dispatch(fetchCheckoutData(params, mafiaEndpoints));
          }
        }
      })
      .catch(catchHandlerFactory(dispatch, CHECKOUT_STEP_MAP[TBD_STEP]));
  };
}

// to handle case of a refresh on the edit address page - this will help
// get the address in question loaded back into the form
export function storeSelectedAddress(addressId) {
  return function (dispatch, getState) {
    const { checkoutData: { addresses: { saved: savedAddresses = [] } } } = getState();
    const addressInfo = savedAddresses.find(item => item.addressId === addressId);

    if (!addressInfo) {
      return;
    }

    const {
      mailingAddress: { addressLine1, addressLine2, postalCode, city, stateOrRegion, countryCode },
      name: { fullName },
      phone: { voice: { primary: { number } } }
    } = addressInfo;

    dispatch(receiveNewShippingField('addressId', addressId || ''));
    dispatch(receiveNewShippingField('addressLine1', addressLine1 || ''));
    dispatch(receiveNewShippingField('addressLine2', addressLine2 || ''));
    dispatch(receiveNewShippingField('city', city || ''));
    dispatch(receiveNewShippingField('countryCode', countryCode || ''));
    dispatch(receiveNewShippingField('fullName', fullName || ''));
    dispatch(receiveNewShippingField('postalCode', postalCode || ''));
    dispatch(receiveNewShippingField('primaryVoiceNumber', number || ''));
    dispatch(receiveNewShippingField('stateOrRegion', stateOrRegion || ''));
  };
}

export function clearAddressErrors() {
  return function (dispatch) {
    dispatch(receiveNewShippingField('errors', {}));
    dispatch(setInvalidAddressFields([]));
  };
}

export function clearSelectedAddress() {
  return function (dispatch) {
    dispatch(receiveNewShippingField('addressId', ''));
    dispatch(receiveNewShippingField('addressLine1', ''));
    dispatch(receiveNewShippingField('addressLine2', ''));
    dispatch(receiveNewShippingField('city', ''));
    dispatch(receiveNewShippingField('countryCode', 'US'));
    dispatch(receiveNewShippingField('fullName', ''));
    dispatch(receiveNewShippingField('postalCode', ''));
    dispatch(receiveNewShippingField('primaryVoiceNumber', ''));
    dispatch(receiveNewShippingField('stateOrRegion', ''));
  };
}
